import React from 'react';
import { ActionConfirmation } from './ActionConfirmation';
import type { ActionType, ActionStatus, ActionData } from '../../services/chatService';

interface AIMessageProps {
  messageId: string;
  content: string;
  timestamp: Date;
  actionType?: ActionType | null;
  actionStatus?: ActionStatus | null;
  actionData?: string | null;
  onApproveAction?: (messageId: string) => Promise<void>;
  onRejectAction?: (messageId: string) => Promise<void>;
  onCreateRequirement?: (requirementData: { title: string; description: string; requirement_type: string; priority: string }) => void;
}

export function AIMessage({
  messageId,
  content,
  timestamp,
  actionType,
  actionStatus,
  actionData,
  onApproveAction,
  onRejectAction,
  onCreateRequirement
}: AIMessageProps): React.ReactElement {
  // Parse action data if present (handle potential double-encoding)
  let parsedActionData: ActionData | null = null;
  if (actionData) {
    try {
      parsedActionData = JSON.parse(actionData);

      // Check if we got a string back (double-encoded JSON)
      if (typeof parsedActionData === 'string') {
        console.log('Detected double-encoded JSON, parsing again...');
        parsedActionData = JSON.parse(parsedActionData);
      }
    } catch (error) {
      console.error('Failed to parse action data:', error);
    }
  }

  // Note: Auto-triggering moved to ChatWidget to avoid triggering on historical messages

  return (
    <div className="flex items-start gap-2 mb-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 border border-purple-200 flex items-center justify-center shadow-sm">
        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      </div>
      <div className="flex flex-col max-w-[80%]">
        <div className="bg-gray-100 border border-gray-200 text-gray-900 rounded-2xl rounded-tl-sm px-4 py-2 shadow-sm">
          <p className="text-sm whitespace-pre-wrap break-words">{content}</p>
        </div>
        <span className="text-xs text-gray-400 mt-1 px-2">
          {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>

        {/* Show action confirmation if there's a pending action (but NOT for create_requirement, which auto-opens modal) */}
        {actionType && actionStatus === 'pending' && parsedActionData && onApproveAction && onRejectAction && actionType !== 'create_requirement' && (
          <ActionConfirmation
            messageId={messageId}
            actionType={actionType}
            actionData={parsedActionData}
            onApprove={onApproveAction}
            onReject={onRejectAction}
          />
        )}
      </div>
    </div>
  );
}

export default AIMessage;
